/**
 * @fileoverview Módulos: Treinamentos (admin) e as opções liberadas pela
 * Sttelar, que só o superadministrador vê e muda (o backend confere de novo):
 * Triagem por IA, a retenção de 90 dias dela e o botão Resolver dos atendentes
 * (desligado, só admin encerra conversas; hooks/useTicketCloseButton).
 */

import React, { useRef, useState } from "react";

import SettingsSection from "../../../components/Settings/SettingsSection";
import SettingItem from "../../../components/Settings/SettingItem";
import ConfirmationModal from "../../../components/ConfirmationModal";
import { formatDateTime } from "../../../components/Triage/helpers";
import { invalidateTriageEnabled } from "../../../hooks/useTriageEnabled";
import { i18n } from "../../../translate/i18n";
import { bindSetting } from "../useSettingsArea";
import { findItem, visibleFields } from "../registry";

const t = (key, options) => i18n.t(`settings.sections.modules.${key}`, options);

// Resumo da última limpeza diária (gravado pelo job, só números).
const retentionSummary = (raw) => {
  let report = null;
  try {
    report = raw ? JSON.parse(raw) : null;
  } catch (err) {
    report = null;
  }
  if (!report || !report.at) return t("fields.triageRetention.never");
  const params = {
    date: formatDateTime(report.at),
    sessions: report.sessions ?? 0,
    files: report.files ?? 0,
    notes: report.notes ?? 0,
    workOrders: report.workOrders ?? 0,
    remaining: report.remaining ?? 0,
  };
  return report.mode === "delete"
    ? t("fields.triageRetention.lastDelete", params)
    : t("fields.triageRetention.lastSimulation", params);
};

const Modules = ({ area, ctx }) => {
  const [confirmRetention, setConfirmRetention] = useState(false);
  const [savingRetention, setSavingRetention] = useState(false);
  const savingRetentionRef = useRef(false);

  const handleTriage = async (value) => {
    // As outras telas leem de novo ao abrir (menu do fluxo, aba do ticket).
    if (await area.save("triageEnabled", value)) invalidateTriageEnabled();
  };

  // Apagar de verdade pede confirmação (não dá para desfazer); voltar à simulação, não.
  const handleRetention = (value) => {
    if (value === "enabled") {
      setConfirmRetention(true);
      return;
    }
    area.save("triageRetentionDelete", "disabled");
  };

  // O modal fica aberto (e o Confirmar travado) até gravar: um segundo clique não grava de novo.
  const confirmRetentionDelete = async () => {
    if (savingRetentionRef.current) return;
    savingRetentionRef.current = true;
    setSavingRetention(true);
    try {
      await area.save("triageRetentionDelete", "enabled");
    } finally {
      savingRetentionRef.current = false;
      setSavingRetention(false);
      setConfirmRetention(false);
    }
  };

  // Os campos que a pessoa vê (registry.js): com a regra nova, pela permissão.
  const fields = visibleFields(findItem("modules"), ctx).map((field) => field.id);

  return (
    <>
      {fields.includes("enableAcademy") && (
        <SettingsSection title={t("groups.features.title")}>
          <SettingItem
            toggle
            {...bindSetting(area, "enableAcademy", "enabled")}
            title={t("fields.enableAcademy.title")}
            description={t("fields.enableAcademy.description")}
          />
        </SettingsSection>
      )}

      {ctx.canEditSuperSettings && (
        <SettingsSection title={t("groups.sttelar.title")} description={t("groups.sttelar.description")}>
          <SettingItem
            toggle
            {...bindSetting(area, "triageEnabled", "disabled")}
            onChange={handleTriage}
            title={t("fields.triageEnabled.title")}
            description={t("fields.triageEnabled.description")}
          />
          <SettingItem
            toggle
            {...bindSetting(area, "triageRetentionDelete", "disabled")}
            id="triageRetention"
            onChange={handleRetention}
            title={t("fields.triageRetention.title")}
            description={`${t("fields.triageRetention.description")} ${retentionSummary(
              area.get("triageRetentionReport", null)
            )}`}
          />
          <SettingItem
            toggle
            {...bindSetting(area, "ticketCloseButton", "enabled")}
            title={t("fields.ticketCloseButton.title")}
            description={t("fields.ticketCloseButton.description")}
          />
          <ConfirmationModal
            title={t("fields.triageRetention.confirmTitle")}
            open={confirmRetention}
            onClose={() => setConfirmRetention(false)}
            onConfirm={confirmRetentionDelete}
            confirmLoading={savingRetention}
          >
            {t("fields.triageRetention.confirmBody")}
          </ConfirmationModal>
        </SettingsSection>
      )}
    </>
  );
};

export default Modules;
